

















































































































import { Component, Vue } from 'vue-property-decorator'
import SorewardsCaretakerSelect from '@/views/sorewards/components/caretaker-select.vue'
import {
  deleteNegotiatedRate,
  getStandardRates,
  listNegotiatedRates,
  saveNegotiatedRate
} from '@/services/sorewards/console'
import { NegotiatedRates, RATE_GRID_KEYS, RateGrid, StandardRates } from '@/services/sorewards/types'

@Component({
  name: 'SorewardsNegotiatedRates',
  components: {
    SorewardsCaretakerSelect
  }
})
export default class extends Vue {
  private loading = true
  private saving = false
  private rows: NegotiatedRates[] = []
  private standard: StandardRates | null = null
  private editId: string | null = null
  private draft: NegotiatedRates = this.emptyDraft()
  private rateKeys = RATE_GRID_KEYS

  async created() {
    await this.fetchAll()
  }

  private emptyDraft(): NegotiatedRates {
    return {
      id: '',
      caretakerId: '',
      caretakerName: '',
      validUntil: '2027-12-31',
      issuePhysical: 0,
      issueVirtual: 0,
      shipUnit: 0,
      shipGrouped: 0,
      loadFlat: 0,
      loadPct: 0
    }
  }

  private async fetchAll() {
    this.loading = true
    try {
      const [standard, rows] = await Promise.all([
        getStandardRates(),
        listNegotiatedRates()
      ])
      this.standard = standard
      this.rows = rows
    } finally {
      this.loading = false
    }
  }

  private isEditing(row: NegotiatedRates) {
    return this.editId === row.id
  }

  private addRow() {
    const grid = this.standard || this.emptyDraft()
    const row: NegotiatedRates = {
      id: `draft-${Date.now()}`,
      caretakerId: '',
      caretakerName: '',
      validUntil: '2027-12-31',
      issuePhysical: grid.issuePhysical,
      issueVirtual: grid.issueVirtual,
      shipUnit: grid.shipUnit,
      shipGrouped: grid.shipGrouped,
      loadFlat: grid.loadFlat,
      loadPct: grid.loadPct
    }
    this.rows = [...this.rows, row]
    this.startEdit(row)
  }

  private startEdit(row: NegotiatedRates) {
    this.editId = row.id
    this.draft = { ...row }
  }

  private cancel() {
    if (this.editId && this.editId.startsWith('draft-')) {
      this.rows = this.rows.filter(row => row.id !== this.editId)
    }
    this.editId = null
    this.draft = this.emptyDraft()
  }

  private onDraftCaretaker(id: string) {
    this.draft.caretakerId = id || ''
  }

  private async save() {
    if (!this.draft.caretakerId && !this.draft.caretakerName) {
      this.$notify({
        title: String(this.$t('sorewardsConsole.caretakerRequired')),
        message: '',
        type: 'warning',
        duration: 3000
      })
      return
    }
    if (!this.draft.validUntil) {
      this.$notify({
        title: String(this.$t('sorewardsConsole.validUntilRequired')),
        message: '',
        type: 'warning',
        duration: 3000
      })
      return
    }

    this.saving = true
    try {
      const payload = { ...this.draft }
      if (payload.id.startsWith('draft-')) {
        payload.id = ''
      }
      await saveNegotiatedRate(payload)
      this.editId = null
      this.draft = this.emptyDraft()
      this.rows = await listNegotiatedRates()
      this.$notify({
        title: String(this.$t('sorewardsConsole.saved')),
        message: '',
        type: 'success',
        duration: 2500
      })
    } finally {
      this.saving = false
    }
  }

  private async remove(row: NegotiatedRates) {
    await deleteNegotiatedRate(row.id)
    this.rows = await listNegotiatedRates()
  }

  private rateLabel(key: keyof RateGrid) {
    const map: Record<keyof RateGrid, string> = {
      issuePhysical: 'physCard',
      issueVirtual: 'virtCard',
      shipUnit: 'shipUnit',
      shipGrouped: 'shipGroup',
      loadFlat: 'loadFee',
      loadPct: 'loadPct'
    }
    return this.$t(`sorewardsConsole.${map[key]}`)
  }

  private formatRate(key: keyof RateGrid, value: number) {
    if (key === 'loadPct') return this.$n(value / 100, 'percent')
    return this.$n(value, 'currency')
  }

  private delta(row: NegotiatedRates, key: keyof RateGrid) {
    if (!this.standard) return 0
    return row[key] - this.standard[key]
  }

  private deltaLabel(row: NegotiatedRates, key: keyof RateGrid) {
    if (!this.standard) return ''
    const base = this.standard[key] || 1
    const pct = Math.round(this.delta(row, key) / base * 100)
    return `${pct > 0 ? '+' : ''}${pct} %`
  }
}
